import { Disclosure } from '@headlessui/react';
import { IconButton } from '@mui/material';
import type { OneTimeProduct } from '@weft/services';
import type { PaymentTerm } from '@weft/types';
import { PAYMENT_TERMS } from '@weft/types';
import { isEmptyString, isNullOrUndefined, raiseUnreachable, removeInIndex, values } from '@weft/utils';
import { Checkbox, Select } from 'antd';
import cn from 'classnames';
import type { DefaultOptionType } from 'rc-select/lib/Select';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Divider } from '../../../../../../../components/design/divider/Divider';
import { Input } from '../../../../../../../components/design/Input';
import { DisclosureIcon } from '../../../../../../../components/icons/DisclosureIcon';
import { TrashIcon } from '../../../../../../../components/icons/TrashIcon';
import { useCreatePlanContext } from '../../../../../../../contexts/create-plan-context';
import { vyFormRegister } from '../../../../../../../utils/react-hook-forms-utils';
import { useChargeContext } from '../../../charge-context';
import type { NewPlanForm } from '../../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';

export const CommercialTermsChargeEdit: React.FC = () => {
  const { itemIndex: productIndex } = useChargeContext<OneTimeProduct>();
  const { control, getValues, register, setValue, getFieldState } = useFormContext<PlanInputForm, NewPlanForm>();
  const { formUtils: { isFieldEditable } } = useCreatePlanContext();

  const isCommercialTermsEditable = isFieldEditable(`fixedProducts.${productIndex}`);

  const nameRegister = vyFormRegister(register, `fixedProducts.${productIndex}.variants.0.name`);
  const amountRegister = register(`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.price`, {
    setValueAs: (value) => isEmptyString(value) || isNaN(value) || isNullOrUndefined(value) || value === 0 ? undefined : Number(value),
  });

  const handleDeleteCommitment = () => {
    const updatedProducts = removeInIndex(getValues('fixedProducts'), productIndex);
    setValue('fixedProducts', updatedProducts);
  };

  return (
    <section className="space-y-5">
      <div className="text-lg font-medium text-black">Commercial terms</div>
      <div className={cn('border-frame rounded-2lg w-full border bg-white px-10 py-8', { 'opacity-50': !isCommercialTermsEditable })}>
        <Disclosure defaultOpen={!isCommercialTermsEditable}>
          <Disclosure.Button
            className="flex w-full items-center justify-between"
          >
            <p className="text-filled-text text-lg">
              Add commitment
            </p>
            <DisclosureIcon/>
          </Disclosure.Button>
          <Disclosure.Panel>
            <div className="mt-5 flex w-full gap-x-6">
              <div className="flex w-72">
                <Input
                  label='Description'
                  placeholder='such as: Yearly commitment'
                  debounce={false}
                  errorMessage={getFieldState(nameRegister.name)?.error?.message}
                  value={getValues(nameRegister.name)}
                  {...nameRegister}
                  disabled={!isCommercialTermsEditable}
                />
              </div>

              <div className="flex w-36">
                <Input
                  label='Amount'
                  prefix="$"
                  type="number"
                  step="any"
                  debounce={false}
                  errorMessage={getFieldState(amountRegister.name)?.error?.message}
                  value={getValues(amountRegister.name) ?? ''}
                  { ...amountRegister }
                  disabled={!isCommercialTermsEditable}
                />
              </div>

              {isCommercialTermsEditable && (
                <div className='fill-icon flex grow items-center justify-end'>
                  <IconButton onClick={handleDeleteCommitment}>
                    <TrashIcon />
                  </IconButton>
                </div>
              )}
            </div>

            <Divider className="my-5"/>

            <div className="flex flex-col gap-y-5">
              <Controller
                name={`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.shouldCreateSeparateInvoice`}
                control={control}
                render={({ field: { value, ...field } }) => (
                  <Checkbox
                    className="text-label font-sans"
                    checked={getValues(`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.shouldCreateSeparateInvoice`)}
                    {...field}
                    disabled={!isCommercialTermsEditable}
                  >
                    Issue the commitment amount as a separate invoice
                  </Checkbox>
                )}
              />
            </div>

            <Divider className="my-5"/>

            <div className="flex gap-x-2.5">
              <div className="flex flex-1 flex-col">
                <label className="flex justify-between">
                  <span className='text-label text-sm'>Payment terms *(required)</span>
                </label>

                <Controller
                  name={`fixedProducts.${productIndex}.paymentTerm`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      className="product-source-select mt-2 w-full truncate"
                      placeholder="Select payment term"
                      options={PAYMENT_TERMS_SELECT_OPTIONS}
                      value={getValues(`fixedProducts.${productIndex}.paymentTerm`)}
                      disabled={!isCommercialTermsEditable}
                    />
                  )}
                />
              </div>

              <div className="flex flex-1 opacity-50">
                <Input
                  label='Bank Transaction Approval'
                  placeholder='Approval # From Bank Here'
                  disabled
                />
              </div>
            </div>
          </Disclosure.Panel>
        </Disclosure>
      </div>
    </section>
  );
};

const PAYMENT_TERMS_SELECT_OPTIONS: DefaultOptionType[] = values(PAYMENT_TERMS).map(paymentTerm => ({
  label: getPaymentTermLabel(paymentTerm),
  value: paymentTerm,
}));

function getPaymentTermLabel(paymentTerm: PaymentTerm) {
  switch (paymentTerm) {
    case PAYMENT_TERMS.Postpayment:
      return 'Post payment';
    case PAYMENT_TERMS.Prepayment:
      return 'Prepaid payment';
    default:
      return raiseUnreachable(paymentTerm);
  }
}